// iter153 — Pickup location helper for the ride request flow.
//
// Small module that owns:
//   • Mapbox reverse-geocoding (lat,lng → readable address).
//   • Persistence of the most recent detected/selected pickup in
//     localStorage under `habano.ride.lastPickup` so returning users
//     see their last pickup pre-filled.
//   • Persistence of the "pending ride" (destination + source) in
//     sessionStorage under `habano.ride.pending` so the /car/map
//     picker in pickup-mode can hand control back to the ride sheet
//     without losing the destination.
const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const LAST_PICKUP_KEY = "habano.ride.lastPickup";
const PENDING_KEY = "habano.ride.pending";

/** Reverse-geocode a coordinate pair via Mapbox. Falls back to the raw
 *  coords when the API is unreachable or the key is missing.
 *  Returns a compact address string ("Calle 23 esq. L, Vedado"). */
export async function reverseGeocode(lat, lng, lang = "en") {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (!TOKEN) return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  const langParam = lang === "es" ? "es" : "en";
  const url =
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json` +
    `?access_token=${TOKEN}&language=${langParam}&limit=1&types=address,poi,neighborhood`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`http_${res.status}`);
    const data = await res.json();
    const first = data.features?.[0];
    if (!first) return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    // Prefer a compact "<address>, <neighborhood>" when both are present.
    const primary = first.text || first.place_name?.split(",")[0] || "";
    const neighborhood =
      first.context?.find((c) => (c.id || "").startsWith("neighborhood"))?.text
      || first.context?.find((c) => (c.id || "").startsWith("locality"))?.text
      || "";
    if (primary && neighborhood) return `${primary}, ${neighborhood}`;
    return first.place_name || primary || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  } catch {
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  }
}

/** Fire the browser's Geolocation API and return {lat, lng} or throw. */
export function getCurrentCoords({ timeout = 8000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("no_geolocation"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout, maximumAge: 60 * 1000 },
    );
  });
}

export function readLastPickup() {
  try {
    const raw = localStorage.getItem(LAST_PICKUP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Discard pickups older than 6 hours — likely a stale coordinate.
    if (Date.now() - (parsed.ts || 0) > 6 * 60 * 60 * 1000) return null;
    return parsed;
  } catch { return null; }
}

export function writeLastPickup(pickup) {
  if (!pickup) return;
  try {
    localStorage.setItem(LAST_PICKUP_KEY, JSON.stringify({ ...pickup, ts: Date.now() }));
  } catch { /* ignore quota */ }
}

/** Hand off the current ride-request state to the /car/map pickup
 *  picker so it can re-open the ride sheet with the chosen pickup
 *  once the user taps "Set Pickup Here". */
export function stashPendingRide(state) {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}
export function readPendingRide() {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function clearPendingRide() {
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
}
